import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { LOGO_URL, api, centsToUSD } from "@/lib/api";
import { useT } from "@/context/ContentContext";
import { getEventTypeMeta, formatEventDate, formatEventRange } from "@/lib/events";
import { Sparkles, Heart, CalendarDays, MessageCircle, ShoppingBag, ArrowRight, MapPin } from "lucide-react";

const HERO_BG = "https://customer-assets.emergentagent.com/job_dazzlin-dymes-portal/artifacts/erqa9pgh_IMG_7820.jpeg";
const GALLERY_1 = "https://customer-assets.emergentagent.com/job_dazzlin-dymes-portal/artifacts/me8djtp9_IMG_7822.jpeg";
const GALLERY_2 = "https://customer-assets.emergentagent.com/job_dazzlin-dymes-portal/artifacts/ss4u62ae_IMG_7823.jpeg";

export default function Landing() {
  const t = useT();
  const [plans, setPlans] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/tuition").then((r) => setPlans(r.data.slice(0, 3))).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const features = [
    { icon: MessageCircle, title: t("landing.features.card1.title", "Weekly Updates"), body: t("landing.features.card1.body", "") },
    { icon: CalendarDays,  title: t("landing.features.card2.title", "Tuition, Simplified"), body: t("landing.features.card2.body", "") },
    { icon: ShoppingBag,   title: t("landing.features.card3.title", "Shop the Squad"), body: t("landing.features.card3.body", "") },
    { icon: Heart,         title: t("landing.features.card4.title", "Family-First"), body: t("landing.features.card4.body", "") },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        {(() => {
          const videoUrl = t("landing.hero.video_url", "");
          const poster = t("landing.hero.video_poster", "");
          if (videoUrl) {
            return (
              <video
                key={videoUrl}
                src={videoUrl}
                poster={poster || undefined}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="absolute inset-0 -z-10 h-full w-full object-cover opacity-70"
                data-testid="hero-video"
              />
            );
          }
          return (
            <div
              className="absolute inset-0 -z-10 opacity-70"
              style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center 35%" }}
            />
          );
        })()}
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-black/70 via-black/75 to-[#050505]" />
        <div className="relative grain">
          <div className="mx-auto max-w-7xl px-6 pt-20 pb-28 grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-up">
              <div className="overline mb-4">{t("landing.hero.overline", "")}</div>
              <h1 className="font-display uppercase leading-[0.9] text-5xl sm:text-6xl lg:text-7xl">
                <span className="text-chrome">{t("landing.hero.headline_top", "")}</span>
                <br />
                <span className="text-diamond">{t("landing.hero.headline_accent", "")}</span>
              </h1>
              <p className="mt-6 max-w-lg text-white/70 text-base sm:text-lg leading-relaxed">
                {t("landing.hero.subtitle", "")}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/join" className="btn-electric" data-testid="hero-cta-join">
                  {t("landing.hero.cta_primary", "Try Out for the Team")} <ArrowRight size={16} />
                </Link>
                <Link to="/login" className="btn-ghost" data-testid="hero-cta-login">
                  {t("landing.hero.cta_secondary", "Parent Login")}
                </Link>
              </div>
            </div>

            <div className="relative flex items-center justify-center" style={{ filter: "drop-shadow(0 0 60px rgba(0,229,255,0.35))" }}>
              <div className="absolute inset-0 m-auto h-72 w-72 rounded-full bg-[#00E5FF]/10 blur-3xl animate-pulse-glow" />
              <img src={LOGO_URL} alt="Dazzlin' Dymes logo" className="relative w-[min(92%,440px)] object-contain" data-testid="hero-logo" />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="overline mb-3">{t("landing.features.overline", "")}</div>
        <h2 className="font-display uppercase text-3xl sm:text-4xl text-chrome max-w-2xl">
          {t("landing.features.title", "")}
        </h2>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <div key={i} className="card-dark p-6 relative grain" data-testid={`feature-card-${i + 1}`}>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-md grid place-items-center bg-gradient-to-br from-[#0055FF] to-[#00E5FF] text-white">
                  <f.icon size={18} />
                </div>
                <div className="font-varsity text-xl tracking-wider">{f.title}</div>
              </div>
              <p className="mt-4 text-white/65 leading-relaxed text-sm">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* GALLERY STRIP */}
      <section className="relative">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2 relative aspect-[16/9] overflow-hidden rounded-2xl border border-white/10">
              <img src={GALLERY_1} alt="" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <div className="overline">{t("landing.gallery.large.overline", "")}</div>
                <div className="font-display text-3xl text-chrome">{t("landing.gallery.large.title", "")}</div>
              </div>
            </div>
            <div className="relative aspect-[16/9] md:aspect-auto overflow-hidden rounded-2xl border border-white/10">
              <img src={GALLERY_2} alt="" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="overline">{t("landing.gallery.small.overline", "")}</div>
                <div className="font-display text-2xl text-chrome leading-tight">{t("landing.gallery.small.title", "")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TUITION TEASER */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="overline mb-3">{t("landing.tuition.overline", "")}</div>
            <h2 className="font-display uppercase text-3xl sm:text-4xl text-chrome">{t("landing.tuition.title", "")}</h2>
          </div>
          <Link to="/tuition" className="btn-ghost" data-testid="see-all-tuition">See all plans <ArrowRight size={14} /></Link>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {plans.length === 0
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="card-dark p-7 h-48 animate-pulse" data-testid={`tuition-skeleton-${i}`} />
              ))
            : plans.map((p) => (
                <div key={p.id} className="card-dark p-7 relative" data-testid={`tuition-preview-${p.id}`}>
                  <div className="flex items-center gap-2">
                    <Sparkles className="text-[#00E5FF]" size={16} />
                    <div className="font-varsity text-2xl tracking-wider">{p.name}</div>
                  </div>
                  <div className="mt-3 font-display text-4xl text-chrome">{centsToUSD(p.price_cents)}<span className="text-base text-white/40 font-body normal-case tracking-normal"> / {p.cadence}</span></div>
                  <p className="mt-3 text-white/65 text-sm leading-relaxed">{p.description}</p>
                </div>
              ))}
        </div>
      </section>

      {/* UPCOMING EVENTS TEASER */}
      {events.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 pb-8" data-testid="landing-events-teaser">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="overline mb-3">{t("landing.events.overline", "Next Up")}</div>
              <h2 className="font-display uppercase text-3xl sm:text-4xl text-chrome">{t("landing.events.title", "What's On the Calendar")}</h2>
            </div>
            <Link to="/events" className="btn-ghost" data-testid="see-all-events">See full schedule <ArrowRight size={14} /></Link>
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {events.map((ev) => {
              const meta = getEventTypeMeta(ev.event_type);
              const d = formatEventDate(ev.starts_at, ev.all_day);
              return (
                <article key={ev.id} className="card-dark p-6 flex gap-4" data-testid={`landing-event-${ev.id}`}>
                  <div className="shrink-0 w-16 text-center rounded-lg border border-white/10 bg-white/5 py-2 self-start">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#00E5FF]">{d.month}</div>
                    <div className="font-display text-3xl text-chrome leading-none mt-1">{d.day}</div>
                  </div>
                  <div className="min-w-0">
                    <span className={`inline-block text-[10px] uppercase tracking-[0.18em] border px-2 py-0.5 rounded-full ${meta.accent}`}>{meta.label}</span>
                    <div className="font-varsity text-lg tracking-wider mt-2 truncate">{ev.title}</div>
                    <div className="text-xs text-white/55 mt-1 flex items-center gap-1.5">
                      <CalendarDays size={11} /> {formatEventRange(ev.starts_at, ev.ends_at, ev.all_day)}
                    </div>
                    {ev.location && (
                      <div className="text-xs text-white/45 mt-1 flex items-center gap-1.5 truncate">
                        <MapPin size={11} /> {ev.location}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {/* CTA STRIP */}
      <section className="mx-auto max-w-7xl px-6 pb-20">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 p-10 sm:p-16 grain"
             style={{ background: "linear-gradient(120deg, rgba(0,85,255,0.18), rgba(0,229,255,0.10))" }}>
          <div className="relative z-10 max-w-2xl">
            <h2 className="font-display uppercase text-3xl sm:text-5xl text-chrome leading-[0.95]">
              {t("landing.cta.title", "")}
            </h2>
            <p className="mt-4 text-white/75 leading-relaxed">
              {t("landing.cta.body", "")}
            </p>
            <div className="mt-7 flex flex-wrap gap-4">
              <Link to="/register" className="btn-electric" data-testid="cta-register-bottom">
                {t("landing.cta.primary", "Create Account")} <ArrowRight size={16} />
              </Link>
              <Link to="/shop" className="btn-ghost" data-testid="cta-shop-bottom">
                {t("landing.cta.secondary", "Browse Shop")}
              </Link>
            </div>
          </div>
          <Sparkles className="absolute -right-8 -top-8 h-48 w-48 text-[#00E5FF]/10" />
        </div>
      </section>

      <Footer />
    </div>
  );
}
